import { VIA_WRITE_IN_VIA_STATES, viaWriteStatusLabel } from '../../../shared/status';
import { SOURCE_DEMO, SOURCE_TMS } from '../recordSource';
import {
  STATUS_ASSIGNED,
  STATUS_CARRIER_ASSIGNED,
  STATUS_COMPLETED,
  STATUS_HANDED_TO_TRADE,
  STATUS_INACTIVE,
  STATUS_SOLD_BY_TRADE,
  planningStatusAppearance,
  type DictionaryStatus,
} from '../planningStatus';
import { TMS_ORDER_STATUS_LABELS } from '../tmsOrderStatus';

/**
 * Pasek etapów okna zlecenia (makieta FTL p. Mariusza, pkt 12: „CS,
 * Administracja, Zapis TMS, Planowanie FTL, Planowanie Trade, Realizacja,
 * Rozliczenie”; Etap 1, decyzja Bartka 15.10.2026).
 *
 * ## Skąd stan etapu
 *
 * Każdy etap czyta **swoją oś** — nie sklejamy ich w jeden stan (koncepcja
 * okna zlecenia: „pięć osi zamiast jednej listy”, a przepływ nie jest
 * liniowy, więc kilka etapów bywa naraz „w toku”, np. prezlecenie planowane
 * przed zapisem do TMS):
 *
 * - zapis do TMS — źródło rekordu, stan akceptacji i koperta zapisu do VIA;
 * - planowanie FTL i Trade — stan planowania i jego dział ze słownika;
 * - realizacja — status zlecenia w TMS (W / P / C / Z; `C` to realizacja,
 *   nie anulacja — 28.09.2026).
 *
 * Etapów CS, Administracji i rozliczenia aplikacja jeszcze nie prowadzi —
 * są „brak danych” z wyjaśnieniem, nie „niezrobione” (nie zgadujemy, czy
 * zlecenie przez nie przeszło). Giełdy idą osobno, w pasku podsumowania —
 * osobno każda giełda.
 */
export type StageState = 'done' | 'current' | 'todo' | 'noData';

export type StageKey = 'cs' | 'admin' | 'tms' | 'ftl' | 'trade' | 'execution' | 'settlement';

export interface OrderStage {
  key: StageKey;
  label: string;
  state: StageState;
  /** Zdanie dla dymka i czytnika ekranu — skąd stan albo czemu go nie ma. */
  detail: string;
}

export const STAGE_STATE_LABELS: Record<StageState, string> = {
  done: 'zakończony',
  current: 'w toku',
  todo: 'jeszcze nie',
  noData: 'brak danych',
};

/** Etapy, których aplikacja jeszcze nie prowadzi — wyjaśnienie, skąd przyjdą. */
export const NO_DATA_EXPLANATIONS = {
  cs: 'Etapu Customer Service aplikacja jeszcze nie prowadzi — moduł CS powstanie w kolejnym etapie projektu. Nie wiemy, czy i kiedy zlecenie przeszło przez CS.',
  admin:
    'Etapu Administracji (kontrahent, spółka, cena i VAT przed zapisem do TMS) aplikacja jeszcze nie prowadzi — moduł powstanie w kolejnym etapie projektu.',
  settlement:
    'Rozliczenia (faktury, dokumenty) aplikacja nie odczytuje — przyjdą z modułu Administracji w kolejnym etapie projektu.',
} as const;

/** Stany działu Trade, gdy słownik nie podał działu (kopia z kodu). */
const TRADE_CODES: readonly string[] = [STATUS_HANDED_TO_TRADE, STATUS_SOLD_BY_TRADE];

/** Wpis słownika z działem (`GET /user-offers/states`). */
export type DictionaryStatusWithDepartment = DictionaryStatus & { department?: string | null };

/**
 * Dział stanu: ze słownika, a bez niego — kopia z kodu. Nieaktualne
 * i Zrealizowane (19.10.2026) — bez działu, wspólne dla FTL i Trade.
 */
export function statusDepartment(
  code: unknown,
  states: ReadonlyArray<DictionaryStatusWithDepartment> = [],
): 'FTL' | 'TRADE' | null {
  if (typeof code !== 'string' || code === '') return null;
  if (code === STATUS_INACTIVE || code === STATUS_COMPLETED) return null;
  const entry = states.find((s) => s.code === code);
  if (entry?.department === 'TRADE') return 'TRADE';
  if (entry?.department === 'FTL') return 'FTL';
  return TRADE_CODES.includes(code) ? 'TRADE' : 'FTL';
}

type Loose = Record<string, unknown>;
const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

/** Zapis do VIA potwierdzony po stronie TMS. */
const VIA_IN_TMS = VIA_WRITE_IN_VIA_STATES;

function tmsStage(r: Loose): OrderStage {
  const base = { key: 'tms' as const, label: 'Zapis do TMS' };
  const number = text(r.externalId);
  if (r.sourceSystem === SOURCE_DEMO) {
    return { ...base, state: 'noData', detail: 'Zlecenie pokazowe — nie pochodzi z TMS.' };
  }
  const envelope = r.order as Loose | undefined;
  const viaStatus = text(envelope?.viaWriteStatus);
  // Prezlecenie połączone ze zleceniem z TMS (16.10.2026, import po naszym
  // numerze albo ręcznie): wiersz ma już źródło TMS, ale zlecenie wyszło
  // z aplikacji — mówimy to wprost. „Jest w TMS” (decyzja użytkownika
  // 16.10.2026), nie „przyjęte”: kto je tam wprowadził, nie wiemy.
  if (r.sourceSystem === SOURCE_TMS && viaStatus === 'CONFIRMED_IN_TMS') {
    return {
      ...base,
      state: 'done',
      detail: `Zlecenie z prezlecenia jest w TMS${number ? ` jako ${number}` : ''} — zmiana wymaga usunięcia w VIA i wprowadzenia ponownie.`,
    };
  }
  if (r.sourceSystem === SOURCE_TMS) {
    return {
      ...base,
      state: 'done',
      detail: `Zlecenie odczytane z TMS${number ? ` (numer ${number})` : ''}.`,
    };
  }
  if (r.acceptanceStatus === 'in_tms' || text(r.tmsOrderStatus) || (viaStatus && VIA_IN_TMS.includes(viaStatus))) {
    return { ...base, state: 'done', detail: 'Zlecenie jest w TMS — zapisane z aplikacji.' };
  }
  if (r.acceptanceStatus === 'failed_push_tms') {
    return { ...base, state: 'current', detail: 'Zapis do TMS się nie udał — szczegóły w zapisie do VIA.' };
  }
  if (viaStatus) {
    return { ...base, state: 'current', detail: `Zapis do VIA: ${viaWriteStatusLabel(viaStatus).toLowerCase()}.` };
  }
  if (r.acceptanceStatus === 'save_to_tms') {
    return { ...base, state: 'current', detail: 'Czeka na zapis do TMS.' };
  }
  return { ...base, state: 'todo', detail: 'Zlecenia nie ma jeszcze w TMS.' };
}

function ftlStage(r: Loose, states: ReadonlyArray<DictionaryStatusWithDepartment>): OrderStage {
  const base = { key: 'ftl' as const, label: 'Planowanie · FTL' };
  const code = r.planningStatus;
  const label = planningStatusAppearance(code, states).label;
  if (code === STATUS_INACTIVE) {
    return { ...base, state: 'todo', detail: `Stan „${label}” — zlecenie nie jest planowane.` };
  }
  // Zakończone w TMS (status Z, decyzja użytkownika 19.10.2026) — planowanie
  // za nami, niezależnie od tego, kto wiózł.
  if (code === STATUS_COMPLETED) {
    return { ...base, state: 'done', detail: `Stan „${label}” — zlecenie zakończone w TMS.` };
  }
  const department = statusDepartment(code, states);
  if (department === 'TRADE') {
    return { ...base, state: 'done', detail: 'Planowanie FTL zakończone przekazaniem do Trade.' };
  }
  if (code === STATUS_ASSIGNED || code === STATUS_CARRIER_ASSIGNED) {
    return { ...base, state: 'done', detail: `Stan „${label}”.` };
  }
  if (department === 'FTL') return { ...base, state: 'current', detail: `Stan „${label}”.` };
  return { ...base, state: 'todo', detail: 'Brak stanu planowania.' };
}

function tradeStage(r: Loose, states: ReadonlyArray<DictionaryStatusWithDepartment>): OrderStage {
  const base = { key: 'trade' as const, label: 'Planowanie · Trade' };
  const code = r.planningStatus;
  // Zrealizowane nie mówi, czy zlecenie przeszło przez Trade (stan wspólny) —
  // przewoźnik wpisany w TMS mówi, że wiózł podwykonawca.
  if (code === STATUS_COMPLETED) {
    const label = planningStatusAppearance(code, states).label;
    return text(r.tmsCarrierTitle)
      ? { ...base, state: 'done', detail: `Stan „${label}” — zlecenie wykonał przewoźnik wpisany w TMS.` }
      : { ...base, state: 'todo', detail: `Stan „${label}” — w TMS bez przewoźnika.` };
  }
  if (statusDepartment(code, states) !== 'TRADE') {
    return { ...base, state: 'todo', detail: 'Zlecenia nie przekazano do Trade.' };
  }
  const label = planningStatusAppearance(code, states).label;
  if (code === STATUS_SOLD_BY_TRADE) {
    return { ...base, state: 'done', detail: `Stan „${label}” — przewoźnik wpisany w TMS.` };
  }
  return { ...base, state: 'current', detail: `Stan „${label}”.` };
}

function executionStage(r: Loose): OrderStage {
  const base = { key: 'execution' as const, label: 'Realizacja' };
  const code = text(r.tmsOrderStatus);
  if (!code) {
    return {
      ...base,
      state: 'todo',
      detail: 'Stan realizacji przychodzi ze statusu zlecenia w TMS — tego zlecenia TMS jeszcze nie podał.',
    };
  }
  const word = TMS_ORDER_STATUS_LABELS[code];
  const status = word ? `${code} — ${word}` : code;
  if (code === 'C') return { ...base, state: 'current', detail: `Status w TMS: ${status}.` };
  if (code === 'Z') return { ...base, state: 'done', detail: `Status w TMS: ${status}.` };
  return { ...base, state: 'todo', detail: `Realizacja jeszcze się nie zaczęła (status w TMS: ${status}).` };
}

/** Dział z prezlecenia — dopisek do etapów, których aplikacja nie prowadzi. */
function preOrderDeptNote(r: Loose): string {
  const dept = text((r.preOrder as Loose | undefined)?.responsibleDept);
  return dept ? ` Prezlecenie wskazuje dział: ${dept}.` : '';
}

/** Siedem etapów w kolejności z makiety. */
export function orderStages(
  record: Loose | undefined,
  states: ReadonlyArray<DictionaryStatusWithDepartment> = [],
): OrderStage[] {
  const r = record ?? {};
  const deptNote = preOrderDeptNote(r);
  return [
    { key: 'cs', label: 'CS', state: 'noData', detail: NO_DATA_EXPLANATIONS.cs + deptNote },
    { key: 'admin', label: 'Administracja', state: 'noData', detail: NO_DATA_EXPLANATIONS.admin + deptNote },
    tmsStage(r),
    ftlStage(r, states),
    tradeStage(r, states),
    executionStage(r),
    { key: 'settlement', label: 'Rozliczenie', state: 'noData', detail: NO_DATA_EXPLANATIONS.settlement },
  ];
}
